import { forwardRef } from "react";
import { ShieldCheck } from "lucide-react";
import { TrainingTrial, type TrainingTrialHandle } from "@/components/landing/TrainingTrial";
import { cn } from "@/lib/utils";

/**
 * HeroTrialPanel — wraps the public TrainingTrial in slim hero chrome.
 */
type Props = {
  onSignup: () => void;
  badge?: string;
  expanded?: boolean;
  onStateChange?: (kind: "idle" | "loading" | "result" | "error") => void;
};

export const HeroTrialPanel = forwardRef<TrainingTrialHandle, Props>(
  function HeroTrialPanel(
    { onSignup, badge = "Live preview · runs on your URL", expanded = false, onStateChange },
    ref,
  ) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border bg-card/70 backdrop-blur-xl shadow-2xl shadow-primary/5 overflow-hidden transition-all duration-500",
          expanded && "ring-1 ring-primary/15 shadow-primary/10",
        )}
      >
        {/* Hero chrome — slim, single line */}
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-2.5 bg-background/40">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary">
            <span
              aria-hidden
              className="h-1.5 w-1.5 rounded-full bg-primary-glow"
              style={{
                boxShadow: "0 0 8px hsl(var(--primary-glow) / 0.8)",
                animation: "breathe-soft 2.4s ease-in-out infinite",
              }}
            />
            {badge}
          </div>
          <div className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <ShieldCheck className="h-3 w-3" />
            0 signups required
          </div>
        </div>

        {/* Real trial widget — header suppressed; hero copy provides context */}
        <div
          className={cn(
            "transition-all duration-500",
            expanded ? "px-4 sm:px-6 lg:px-8 pb-6 pt-5" : "px-4 pb-4 pt-4",
          )}
        >
          <TrainingTrial
            ref={ref}
            onSignup={onSignup}
            showHeader={false}
            onStateChange={onStateChange}
          />
        </div>
      </div>
    );
  },
);
